import { Component, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { Select2Data } from 'ng-select2-component';
import { Subject, debounceTime, tap, switchMap, Subscription, interval, takeWhile } from 'rxjs';
import { NgIf, NgFor, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault, DecimalPipe } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { USER_PERMISSIONS } from '../../app.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EnquiriesReportRow,
  EnquiriesReportSummary,
  ReportJobStatusResponse
} from '../../models/reports.model';

@Component({
  selector: 'app-enquiries-reports',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PaginationComponent,
    BreadcrumbComponent,
    NgSelectModule,
    NgIf,
    NgFor,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    DecimalPipe
  ],
  templateUrl: './enquiries-reports.component.html',
  styleUrl: './enquiries-reports.component.css'
})
export class EnquiriesReportsComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Reports',
    currentURL: 'Enquiries',
    breadcrumb_url: '/admin/reports/enquiries'
  };

  // Tab control
  activeTab: 'all' | 'by-status' | 'by-platform' | 'graphs' = 'all';

  // Pagination - All tab
  pageAll: number = 1;
  totalRowsAll: number = 20;
  totalPageAll: number = 0;
  pageCountAll: number = 1;

  // Pagination - By Status tab
  pageByStatus: number = 1;
  totalRowsByStatus: number = 20;
  totalPageByStatus: number = 0;
  pageCountByStatus: number = 1;

  // Pagination - By Platform tab
  pageByPlatform: number = 1;
  totalRowsByPlatform: number = 20;
  totalPageByPlatform: number = 0;
  pageCountByPlatform: number = 1;

  // Data
  allEnquiriesData: EnquiriesReportRow[] = [];
  byStatusData: any[] = [];
  byPlatformData: any[] = [];
  summaryData: EnquiriesReportSummary | null = null;

  // Filter form
  filterForm!: FormGroup;
  disableClear: boolean = false;

  // Property dropdown
  propertiesList: any[] = [];
  propertiesItems: Select2Data = [];
  searchProperty$ = new Subject<string>();
  private propertyPage = 1;
  private propertyPageSize = 20;
  private hasMoreProperties = true;

  // Share modal user search
  managersList: any[] = [];
  managersItems: Select2Data = [];
  searchManager$ = new Subject<string>();
  private managerPage = 1;
  private managerPageSize = 20;
  private hasMoreManagers = true;

  // Available columns (from server) and currently-checked column keys
  availableColumns: { key: string; label: string; default?: boolean; wrap?: boolean }[] = [];
  selectedColumnKeys: Set<string> = new Set();
  showColumnPicker = false;

  // Status options
  statusOptions = [
    { value: 'HOT', label: 'Hot' },
    { value: 'WARM', label: 'Warm' },
    { value: 'COLD', label: 'Cold' },
    { value: 'CONVERTED', label: 'Converted' },
    { value: 'REJECT', label: 'Reject' }
  ];

  // Platform options
  platformOptions = [
    { value: 'Magicbricks', label: 'Magicbricks' },
    { value: '99acres', label: '99acres' },
    { value: 'Housing', label: 'Housing' },
    { value: 'Direct', label: 'Direct' }
  ];

  // Download state
  isDownloading: boolean = false;
  downloadProgress: string = '';
  activeDownloadType: 'pdf' | 'csv' | 'email' | 'whatsapp' | null = null;

  // Share modal state
  showShareModal: boolean = false;
  shareType: 'email' | 'whatsapp' = 'email';
  shareForm!: FormGroup;

  reportTypeOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'csv', label: 'CSV' }
  ];

  // Services
  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);

  // Subscriptions
  private allReportSubscription?: Subscription;
  private byStatusSubscription?: Subscription;
  private byPlatformSubscription?: Subscription;
  private propertySearchSubscription?: Subscription;
  private managerSearchSubscription?: Subscription;

  constructor(
    private fb: FormBuilder,
    private alertService: NgxToastrService
  ) {
    this.initFilterForm();
    this.initShareForm();
  }

  ngOnInit(): void {
    this.setupPropertySearch();
    this.setupManagerSearch();
    this.setupDateValidation();
    this.loadAllReport();
    this.loadByStatusReport();
    this.loadByPlatformReport();
  }

  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      start_date: [''],
      end_date: [{ value: '', disabled: true }],
      property_id: [null],
      status: [null],
      platform: [null]
    });
  }

  private initShareForm(): void {
    this.shareForm = this.fb.group({
      selectedUser: [null],
      file_type: ['pdf'],
      first_name: [''],
      last_name: [''],
      email: [''],
      country_code: [''],
      whatsapp: ['']
    });
  }

  private setupDateValidation(): void {
    this.filterForm.get('start_date')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const endDateControl = this.filterForm.get('end_date');
        if (value) {
          endDateControl!.enable();
        } else {
          endDateControl!.disable();
          endDateControl!.reset();
        }
      });
  }

  private setupPropertySearch(): void {
    this.propertySearchSubscription = this.searchProperty$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.propertyPage = 1;
          this.hasMoreProperties = true;
        }),
        switchMap((term) => {
          const query = term ? `&name=${term}` : '';
          return this.httpService.getAllProperties(this.propertyPage, this.propertyPageSize, query);
        })
      )
      .subscribe((response) => {
        this.propertiesList = response.data?.properties ?? [];
        this.hasMoreProperties = this.propertiesList.length < (response.data?.total ?? 0);
        this.propertiesItems = this.propertiesList.map((p: any) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name }
        }));
      });
    this.searchProperty$.next('');
  }

  private setupManagerSearch(): void {
    this.managerSearchSubscription = this.searchManager$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.managerPage = 1;
          this.hasMoreManagers = true;
        }),
        switchMap((term) => {
          const search = term ? term : '';
          return this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize, search);
        })
      )
      .subscribe((response) => {
        this.managersList = response.data?.users ?? [];
        this.hasMoreManagers = this.managersList.length < (response.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
    this.searchManager$.next('');
  }

  onPropertyScrollEnd(): void {
    if (!this.hasMoreProperties) return;
    this.propertyPage++;
    this.httpService.getAllProperties(this.propertyPage, this.propertyPageSize)
      .subscribe((resp) => {
        const more = resp.data?.properties ?? [];
        this.propertiesList = [...this.propertiesList, ...more];
        this.hasMoreProperties = this.propertiesList.length < (resp.data?.total ?? 0);
        this.propertiesItems = this.propertiesList.map((p: any) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name }
        }));
      });
  }

  onShareUserChange(selectedItem: any): void {
    if (selectedItem && selectedItem.data) {
      this.shareForm.patchValue({
        first_name: selectedItem.data.first_name || '',
        last_name: selectedItem.data.last_name || ''
      });
      if (this.shareType === 'email') {
        this.shareForm.patchValue({ email: selectedItem.data.email || '' });
      } else {
        this.shareForm.patchValue({
          country_code: selectedItem.data.country_code || '',
          whatsapp: selectedItem.data.phone || ''
        });
      }
    }
  }

  switchTab(tab: 'all' | 'by-status' | 'by-platform' | 'graphs'): void {
    this.activeTab = tab;
  }

  private buildFilterQuery(): string {
    let query = '';
    const formValue = this.filterForm.getRawValue();

    if (formValue.start_date) query += `&start_date=${formValue.start_date}`;
    if (formValue.end_date) query += `&end_date=${formValue.end_date}`;
    if (formValue.property_id) {
      const propertyId = typeof formValue.property_id === 'object' ? formValue.property_id.value : formValue.property_id;
      if (propertyId) query += `&property_id=${propertyId}`;
    }
    if (formValue.status) query += `&status=${formValue.status}`;
    if (formValue.platform) query += `&platform=${formValue.platform}`;

    if (this.selectedColumnKeys.size && this.availableColumns.length) {
      const cols = this.availableColumns
        .map((c) => c.key)
        .filter((k) => this.selectedColumnKeys.has(k));
      if (cols.length) query += `&columns=${encodeURIComponent(cols.join(','))}`;
    }

    return query;
  }

  get visibleColumns(): { key: string; label: string; wrap?: boolean }[] {
    return this.availableColumns.filter((c) => this.selectedColumnKeys.has(c.key));
  }

  toggleColumn(key: string, checked: boolean): void {
    if (checked) this.selectedColumnKeys.add(key);
    else this.selectedColumnKeys.delete(key);
    this.selectedColumnKeys = new Set(this.selectedColumnKeys);
  }

  toggleColumnPicker(): void {
    this.showColumnPicker = !this.showColumnPicker;
  }

  // The dropdown wrapper calls $event.stopPropagation on its own clicks, so
  // anything that bubbles to the document came from outside it.
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showColumnPicker) this.showColumnPicker = false;
  }

  private hydrateColumns(available: { key: string; label: string; default?: boolean; wrap?: boolean }[] | undefined): void {
    if (!available || this.availableColumns.length) return;
    this.availableColumns = available;
    this.selectedColumnKeys = new Set(available.filter((c) => c.default).map((c) => c.key));
  }

  loadAllReport(): void {
    const query = this.buildFilterQuery();
    this.allReportSubscription = this.httpService
      .getEnquiriesReportAll(this.pageAll, this.totalRowsAll, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.allEnquiriesData = response.data.rows ?? [];
            this.summaryData = response.data.summary ?? null;
            this.totalPageAll = response.data.total ?? 0;
            this.pageCountAll = response.data.pageCount ?? 1;
            this.hydrateColumns(response.data.available_columns);
          }
        },
        error: () => {
          this.alertService.error('Failed to load enquiries report', 'toast-top-right');
        }
      });
  }

  loadByStatusReport(): void {
    const query = this.buildFilterQuery();
    this.byStatusSubscription = this.httpService
      .getEnquiriesReportByStatus(this.pageByStatus, this.totalRowsByStatus, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.byStatusData = response.data.rows ?? [];
            if (!this.summaryData) this.summaryData = response.data.summary ?? null;
            this.totalPageByStatus = response.data.total ?? 0;
            this.pageCountByStatus = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load by-status report', 'toast-top-right');
        }
      });
  }

  loadByPlatformReport(): void {
    const query = this.buildFilterQuery();
    this.byPlatformSubscription = this.httpService
      .getEnquiriesReportByPlatform(this.pageByPlatform, this.totalRowsByPlatform, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.byPlatformData = response.data.rows ?? [];
            if (!this.summaryData) this.summaryData = response.data.summary ?? null;
            this.totalPageByPlatform = response.data.total ?? 0;
            this.pageCountByPlatform = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load by-platform report', 'toast-top-right');
        }
      });
  }

  onFilterChange(): void {
    this.disableClear = true;
    this.pageAll = 1;
    this.pageByStatus = 1;
    this.pageByPlatform = 1;
    this.loadAllReport();
    this.loadByStatusReport();
    this.loadByPlatformReport();
  }

  resetFilters(): void {
    this.filterForm.reset({
      start_date: '',
      end_date: '',
      property_id: null,
      status: null,
      platform: null
    });
    this.disableClear = false;
    this.pageAll = 1;
    this.pageByStatus = 1;
    this.pageByPlatform = 1;
    this.loadAllReport();
    this.loadByStatusReport();
    this.loadByPlatformReport();
  }

  pageChangeAll(page: number): void {
    if (this.pageAll === page) return;
    this.pageAll = page;
    this.loadAllReport();
  }

  pageChangeByStatus(page: number): void {
    if (this.pageByStatus === page) return;
    this.pageByStatus = page;
    this.loadByStatusReport();
  }

  pageChangeByPlatform(page: number): void {
    if (this.pageByPlatform === page) return;
    this.pageByPlatform = page;
    this.loadByPlatformReport();
  }

  downloadReport(fileType: 'pdf' | 'csv'): void {
    if (this.isDownloading) return;

    this.isDownloading = true;
    this.activeDownloadType = fileType;
    this.downloadProgress = 'Initiating...';

    const query = this.buildFilterQuery();
    let downloadFn;

    if (this.activeTab === 'by-status') {
      downloadFn = this.httpService.downloadEnquiriesReportByStatus(fileType, query);
    } else if (this.activeTab === 'by-platform') {
      downloadFn = this.httpService.downloadEnquiriesReportByPlatform(fileType, query);
    } else {
      downloadFn = this.httpService.downloadEnquiriesReportAll(fileType, query);
    }

    downloadFn.subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatus(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate download', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate download', 'toast-top-right');
      }
    });
  }

  private resetDownloadState(): void {
    this.isDownloading = false;
    this.downloadProgress = '';
    this.activeDownloadType = null;
  }

  private pollJobStatus(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;

    this.checkJobStatus(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Checking... (${pollCount})`;
        this.checkJobStatus(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Report generation timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatus(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;
          if (jobData.status === 'completed' && jobData.file_url) {
            this.resetDownloadState();
            this.alertService.success('Report generated successfully!', 'toast-top-right');
            window.open(jobData.file_url, '_blank');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Report generation failed', 'toast-top-right');
          }
        }
      },
      error: () => {}
    });
  }

  openShareModal(type: 'email' | 'whatsapp'): void {
    this.shareType = type;
    this.shareForm.reset({ file_type: 'pdf' });
    this.showShareModal = true;
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this.shareForm.reset({ file_type: 'pdf' });
  }

  submitShare(): void {
    const formValue = this.shareForm.getRawValue();
    const shareValue = this.shareType === 'email' ? formValue.email : formValue.whatsapp;

    if (!shareValue || shareValue.trim() === '') {
      this.alertService.error(
        `Please enter ${this.shareType === 'email' ? 'an email address' : 'a WhatsApp number'}`,
        'toast-top-right'
      );
      return;
    }

    this.closeShareModal();
    this.isDownloading = true;
    this.activeDownloadType = this.shareType;
    this.downloadProgress = 'Sending...';

    const query = this.buildFilterQuery();
    const shareData: any = {
      share_type: this.shareType,
      first_name: formValue.first_name?.trim() || '',
      last_name: formValue.last_name?.trim() || ''
    };

    if (this.shareType === 'email') {
      shareData.email = shareValue.trim();
    } else {
      shareData.country_code = formValue.country_code?.trim() || '';
      shareData.whatsapp = shareValue.trim();
    }

    const fileType = formValue.file_type || 'pdf';
    let downloadFn;

    if (this.activeTab === 'by-status') {
      downloadFn = this.httpService.downloadEnquiriesReportByStatus(fileType, query, shareData);
    } else if (this.activeTab === 'by-platform') {
      downloadFn = this.httpService.downloadEnquiriesReportByPlatform(fileType, query, shareData);
    } else {
      downloadFn = this.httpService.downloadEnquiriesReportAll(fileType, query, shareData);
    }

    downloadFn.subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatusForShare(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate share', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate share', 'toast-top-right');
      }
    });
  }

  private pollJobStatusForShare(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;

    this.checkJobStatusForShare(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Processing... (${pollCount})`;
        this.checkJobStatusForShare(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Share request timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatusForShare(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;
          if (jobData.status === 'completed') {
            this.resetDownloadState();
            this.alertService.success(`Report shared via ${this.shareType} successfully!`, 'toast-top-right');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Failed to share report', 'toast-top-right');
          }
        }
      },
      error: () => {}
    });
  }

  get checkDownloadPermission(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.REPORTS.REPORTS_DOWNLOAD);
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'HOT': 'badge-danger',
      'WARM': 'badge-warning',
      'COLD': 'badge-info',
      'CONVERTED': 'badge-success',
      'REJECT': 'badge-secondary'
    };
    return statusClasses[status] || 'badge-secondary';
  }

  getPlatformClass(platform: string): string {
    const platformClasses: { [key: string]: string } = {
      'Magicbricks': 'badge-danger',
      '99acres': 'badge-warning',
      'Housing': 'badge-primary',
      'Direct': 'badge-success'
    };
    return platformClasses[platform] || 'badge-secondary';
  }

  getSummaryKeys(obj: { [key: string]: number } | undefined): string[] {
    return obj ? Object.keys(obj) : [];
  }

  Math = Math;

  ngOnDestroy(): void {
    this.allReportSubscription?.unsubscribe();
    this.byStatusSubscription?.unsubscribe();
    this.byPlatformSubscription?.unsubscribe();
    this.propertySearchSubscription?.unsubscribe();
    this.managerSearchSubscription?.unsubscribe();
  }
}
